import { Trans } from 'react-i18next';

import { styled } from '@mui/material';

import { useI18n } from '@app-velocity/i18n';
import { RemoveIcon, ArrorUpCircleIcon, RepeatIcon } from '@app-velocity/icons';
import { MessageBus } from '@app-velocity/message-bus';
import { ContextMenu as UIContextMenu } from '@app-velocity/ui';

import { useTeamAdminContext } from '../../../../../../context/useTeamAdminContext';
import {
  useChangeTeamMemberRoleMutation,
  useRemoveTeamMemberMutation,
  useRevokeInvitationMutation,
  useResendInvitationMutation
} from '../../../../../../query';
import { UnifiedTeamMember } from '../../../../../../types';

export type ContextMenuProps = {
  anchor: HTMLElement | null;
  member: UnifiedTeamMember;
  onClose: () => void;
};

const RotatedArrow = styled('div')({
  transform: 'rotate(180deg)',
  display: 'flex',
});

const messageBus = new MessageBus('system');

export const ContextMenu = ({ anchor, member, onClose }: ContextMenuProps) => {
  const { t } = useI18n();
  const { team } = useTeamAdminContext();

  const { mutate: removeTeamMember } = useRemoveTeamMemberMutation(team.id!);

  const { mutate: revokeInvitation } = useRevokeInvitationMutation(team.id!);

  const { mutate: resendInvitation } = useResendInvitationMutation(team.id!);

  const { mutate: changeRole } = useChangeTeamMemberRoleMutation(team.id!);

  const confirmRemoveTeamMember = () => {
    messageBus.publish('modal:action-confirmation:open', {
      title: 'teams:remove_member.title',
      description: (
        <Trans
          i18nKey="teams:remove_member.text"
          values={{ name: member.email }}
          tOptions={{ interpolation: { escapeValue: false } }}
          components={{
            tag: <strong />,
          }}
        />
      ),
      onModalCloseCallback: (decision) => {
        if (decision === 'OK') handleRemoveTeamMember();
      },
      confirmButtonText: 'teams:remove_member.confirm',
      cancelButtonText: 'shared:cancel',
    });
  };

  const confirmInvitationRevoke = () => {
    messageBus.publish('modal:action-confirmation:open', {
      title: 'teams:revoke_invitation.title',
      description: (
        <Trans
          i18nKey="teams:revoke_invitation.text"
          values={{ name: member.email }}
          tOptions={{ interpolation: { escapeValue: false } }}
          components={{
            tag: <strong />,
          }}
        />
      ),
      onModalCloseCallback: (decision) => {
        if (decision === 'OK') handleRevokeInvitation();
      },
      confirmButtonText: 'teams:revoke_invitation.confirm',
      cancelButtonText: 'shared:cancel',
    });
  };

  const upgradeToAdmin = () => {
    changeRole([{ teamMemberId: member.memberId, teamMemberRole: 'ADMIN' }]);
  };

  const downgradeToMember = () => {
    changeRole([{ teamMemberId: member.memberId, teamMemberRole: 'MEMBER' }]);
  };

  const handleResendInvitation = () => {
    if (!member.invitationId) return;
    resendInvitation(member.invitationId);
  };

  const handleRemoveTeamMember = () => {
    removeTeamMember(member.id);
  };

  const handleRevokeInvitation = () => {
    if (!member.invitationId) return;
    revokeInvitation(member.invitationId);
  };

  const handleChangeRole = () => {
    switch (member.role) {
      case 'ADMIN': {
        downgradeToMember();
        break;
      }

      case 'MEMBER': {
        upgradeToAdmin();
        break;
      }
    }
  };

  return (
    <UIContextMenu
      anchor={anchor}
      closeMenu={onClose}
      color="light"
      variant="compact"
    >
      {member.type === 'member' &&
      ['ADMIN', 'MEMBER'].includes(member.role!) ? (
        <>
          <UIContextMenu.Item onClick={handleChangeRole}>
            {member.role === 'ADMIN' ? (
              <RotatedArrow>
                <ArrorUpCircleIcon />
              </RotatedArrow>
            ) : (
              <ArrorUpCircleIcon />
            )}
            {t(
              member.role === 'ADMIN'
                ? 'teams:member_menu.downgrade_to_member'
                : 'teams:member_menu.upgrade_to_admin'
            )}
          </UIContextMenu.Item>
          <UIContextMenu.Divider />
        </>
      ) : null}

      {member.type === 'member' ? (
        <UIContextMenu.Item variant="danger" onClick={confirmRemoveTeamMember}>
          <RemoveIcon />
          {t('teams:member_menu.remove_from_workspace')}
        </UIContextMenu.Item>
      ) : null}
      {member.type === 'invitation'? (
        <>
          <UIContextMenu.Item onClick={handleResendInvitation}>
            <RepeatIcon />
            {t('teams:member_menu.resend_invitation')}
          </UIContextMenu.Item>
          <UIContextMenu.Divider />
        </>
      ) : null}
      {member.type === 'invitation' ? (
        <UIContextMenu.Item variant="danger" onClick={confirmInvitationRevoke}>
          <RemoveIcon />
          {t('teams:member_menu.revoke_invitation')}
        </UIContextMenu.Item>
      ) : null}
    </UIContextMenu>
  );
};
